import { ecId, ecLean, type Row } from '../janus-engine/ability/context.js';
import { normalizePhpListish } from '../shared/php-listish.js';
import { encounterField } from '../shared/array-types.js';
import { departureBefore, departureReferences, departedFrom, resolveServerDeparture } from '../janus-engine/turns/server-departure.js';
import { orbQueueText } from '../janus-engine/ability/orbs/orb-queue.js';
import { prepareTurnTick } from '../janus-engine/turns/tick-frame.js';
import { END_TURN_SCHEMA } from '../janus-engine/turns/end-turn.js';
import { actorEncounterFor, actorOutbox, confirmedRevFor, noteOwnOperation, pendingFor } from './movement-truth.js';
import { prefetchTickDefinitions, turnReaders } from './turn-truth.js';
import { awaitEntityCompileReady } from '../shared/entity-compile-ready.js';
import { ftCurrentTruthSink } from '../shared/truth-sink.js';
import { withLogParticipants } from '../shared/log-participant-snapshot.js';
import { askTriggerConsent, createTriggerConsent, offerFortunate } from './trigger-consent.js';
import { resyncEncounter } from './encounter-delta.js';
import { creatureIsInActiveCombat } from '../roll-init/client-combat-membership.js';
import { orbOperationId } from '../janus-engine/turns/orb-boundary.js';
import { isMovementTruthAcceptedResult } from '../janus-engine/movement/client-truth-commit.js';
import type { ActorTruthCommandBase, MovementTruthRow } from '../janus-engine/movement/client-truth-commit.js';

const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const work = new Map<string, Promise<void>>();
const ENDPOINT = '/options/game/actions/turn_truth_commit_inc.php';
export const departureBoundaryWork = (id: string): Promise<void> | undefined => work.get(upper(id));
const signature = (encounter: Row): string => JSON.stringify(departureBefore(encounter));
function observerFor(encounter: Row): Row | undefined {
  const user = window.FateTide?.user;
  return (normalizePhpListish(encounter.encounter_creatures_arr) as Row[]).find((row: Row) => !Number(ecLean(row, 'encounter_creature_deleted') ?? 0)
    && upper(ecLean(row, 'encounter_id')) === upper(encounterField(encounter, 'encounter_id'))
    && (user?.isGM === true || upper(ecLean(row, 'encounter_creature_created_by_id')) === upper(user?.id))) as Row | undefined;
}

/** Called on installation as well as updates. The saved pointer survives an interval with no observing page. */
export function observeServerDeparture(encounterId: string): void {
  const id = upper(encounterId), encounter = actorEncounterFor(id) as Row | null;
  if (window.top !== window || !window.FateTide?.user || ftCurrentTruthSink() || !encounter || work.has(id)
    || Number(encounterField(encounter, 'in_combat') ?? 0) !== 1 || !observerFor(encounter)) return;
  const roster = normalizePhpListish(encounter.encounter_creatures_arr) as Row[];
  // Missing held rows only request facts; they never authorize cleanup.
  const read = { inCombat: (row: Row) => creatureIsInActiveCombat(encounter, row) };
  if (!departureReferences(encounter).some(ref => {
    const row = roster.find(member => ecId(member) === ref);
    return !row || departedFrom(encounter, row, read);
  })) return;
  if (timers.has(id)) clearTimeout(timers.get(id));
  timers.set(id, setTimeout(() => {
    timers.delete(id);
    const task = settleServerDeparture(id).catch(error => {
      console.warn('[server-departure] settlement failed', error);
    }).finally(() => { work.delete(id); });
    work.set(id, task);
  }, 500));
}

export async function settleServerDeparture(encounterId: string): Promise<void> {
  const encounter = actorEncounterFor(encounterId) as Row | null;
  if (!encounter || ftCurrentTruthSink()) return;
  const observer = observerFor(encounter);
  if (!observer || pendingFor(encounterId, ecId(observer))) return;
  const roster = normalizePhpListish(encounter.encounter_creatures_arr) as Row[];
  if (!await awaitEntityCompileReady([encounterId, ...roster.map(ecId)], ecId(observer))) return;
  const before = signature(encounter), actorId = ecId(observer), operationId = crypto.randomUUID().toUpperCase();
  const factsFor = async (claim = false): Promise<{ departure_rows: Record<string, Row>; claimed?: boolean }> => {
    const response = await fetch(ENDPOINT, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ schema: END_TURN_SCHEMA, encounter_id: encounterId, actor_id: actorId, operation_id: operationId,
        expected_rev: confirmedRevFor(encounterId, actorId, observer as MovementTruthRow), departure_mode: 'facts', departure_ids: departureReferences(encounter),
        ...(claim ? { departure_scope: before } : {}) }) });
    const answer = await response.json() as { ok?: boolean; departure_rows?: Record<string, Row>; claimed?: boolean };
    if (!response.ok || !answer.ok || !answer.departure_rows) throw new Error('departure-facts-unavailable');
    return { departure_rows: answer.departure_rows, claimed: answer.claimed };
  };
  let answer = await factsFor();
  const facts = new Map(Object.entries(answer.departure_rows)), read = turnReaders(encounter);
  if (!departureReferences(encounter).some(id => facts.has(id) && departedFrom(encounter, facts.get(id)!, read))) return;
  await prefetchTickDefinitions(encounter, observer);
  // Two pages may observe the same removal. Claim the opaque saved-state scope before native dice.
  // The existing turn receipt/CAS remains the save authority; the claim expires if this page disappears.
  answer = await factsFor(true);
  if (!answer.claimed) return;
  const consent = createTriggerConsent(encounterId);
  for (;;) {
    // A native action may already have committed this boundary while facts or definitions were arriving.
    if (actorEncounterFor(encounterId) !== encounter || signature(encounter) !== before || pendingFor(encounterId, actorId)) return;
    const decision = resolveServerDeparture(encounter, observer, new Map(Object.entries(answer.departure_rows)), scene => turnReaders(scene, undefined, consent));
    if (decision.kind !== 'plan') {
      if (!consent.final && decision.why.includes('trigger-consent')) { await askTriggerConsent(encounterId, actorId, consent); continue; }
      if (decision.kind === 'unreadable') await resyncEncounter(encounterId);
      return;
    }
    const command = decision.command;
    command.operation_id = operationId;
    command.ending_orb_queue_before = orbQueueText(ecLean(observer, 'orb_queue'));
    const next = roster.find(row => ecId(row) === command.next_id);
    if (!next) return;
    command.next_orb_queue_before = orbQueueText(ecLean(next, 'orb_queue'));
    command.zone_dedupe_claims = []; command.zone_inputs = []; command.start_status_sources ??= [];
    const offers = command.tick?.fortunateOffers;
    if (command.tick) {
      const prepared = prepareTurnTick(roster, command.tick, row => turnReaders(encounter).abilityEntries(row));
      command.tick_before_ec = prepared.before;
      const { fortunateOffers: _offers, ...posted } = command.tick; void _offers;
      command.tick = { ...posted, charges: prepared.charges, statusesAdded: posted.statusesAdded.map(s => ({ ...s, status_id: crypto.randomUUID().toUpperCase() })) };
    }
    if (command.frame.next.refresh !== false) {
      const boundary = { actor_id: command.next_id, round: command.frame.encounter.current_round,
        combat_id: upper(encounterField(encounter, 'active_combat_id')) };
      (command as unknown as Row).orb_channel_claim = { ...boundary, operation_id: await orbOperationId('channel', boundary) };
      if (signature(encounter) !== before || pendingFor(encounterId, actorId)) return;
    }
    const wire = withLogParticipants(command as unknown as ActorTruthCommandBase);
    noteOwnOperation(command.operation_id, END_TURN_SCHEMA);
    const saved = await actorOutbox(encounterId, actorId).enqueue(wire).commit;
    if (isMovementTruthAcceptedResult(saved) && offers?.length) offerFortunate(encounterId, actorId, offers);
    return;
  }
}
Object.assign(window, { ftServerDepartureEvaluate: observeServerDeparture });
